/**
 * PhotoGallery component - displays all photos for a user with draggable circular previews.
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardActions,
  Button,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { Star, StarBorder, Delete, Launch, Save, CloudUpload } from '@mui/icons-material';
import { ProfilePhotoDetail } from '../types';
import { format } from 'date-fns';

interface PhotoGalleryProps {
  photos: ProfilePhotoDetail[];
  onSetPrimary: (photoId: string) => void;
  onDelete: (photoId: string) => void;
  onSaveCropped: (photoId: string, croppedBlob: Blob) => Promise<void>;
  disabled?: boolean;
  uploadingJobs?: Map<string, { jobId: string; tempId: string }>;
}

interface PhotoPosition {
  offset: number;
  isDirty: boolean;
}

const PhotoGallery: React.FC<PhotoGalleryProps> = ({
  photos,
  onSetPrimary,
  onDelete,
  onSaveCropped,
  disabled = false,
  uploadingJobs = new Map(),
}) => {
  const [positions, setPositions] = useState<Record<string, PhotoPosition>>({});
  const [dragging, setDragging] = useState<string | null>(null);
  const [dragStart, setDragStart] = useState(0);
  const [saving, setSaving] = useState<string | null>(null);
  const imageRefs = useRef<Record<string, HTMLImageElement>>({});
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const PREVIEW_SIZE = 180;

  useEffect(() => {
    // Initialize positions for new photos
    photos.forEach(photo => {
      if (!positions[photo.id]) {
        setPositions(prev => ({
          ...prev,
          [photo.id]: { offset: 0, isDirty: false }
        }));
      }
    });
  }, [photos, positions]);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateString: string): string => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch {
      return dateString;
    }
  };

  const handleImageLoad = (photoId: string, img: HTMLImageElement) => {
    imageRefs.current[photoId] = img;
  };

  const getImageDimensions = (photoId: string) => {
    const img = imageRefs.current[photoId];
    if (!img) return { isPortrait: false, scale: 1 };

    const isPortrait = img.naturalHeight > img.naturalWidth;
    const scale = PREVIEW_SIZE / (isPortrait ? img.naturalWidth : img.naturalHeight);

    return { isPortrait, scale };
  };

  const handleMouseDown = (photoId: string, e: React.MouseEvent) => {
    const { isPortrait } = getImageDimensions(photoId);
    if (!isPortrait) return; // Only allow dragging for portrait images

    setDragging(photoId);
    setDragStart(e.clientY - (positions[photoId]?.offset || 0));
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragging) return;

    const newOffset = e.clientY - dragStart;
    const img = imageRefs.current[dragging];
    if (!img) return;

    const { scale } = getImageDimensions(dragging);
    const scaledHeight = img.naturalHeight * scale;
    const maxOffset = (scaledHeight - PREVIEW_SIZE) / 2;

    setPositions(prev => ({
      ...prev,
      [dragging]: {
        offset: Math.max(-maxOffset, Math.min(maxOffset, newOffset)),
        isDirty: true
      }
    }));
  };

  const handleMouseUp = () => {
    setDragging(null);
  };

  const handleTouchStart = (photoId: string, e: React.TouchEvent) => {
    const { isPortrait } = getImageDimensions(photoId);
    if (!isPortrait) return;

    const touch = e.touches[0];
    setDragging(photoId);
    setDragStart(touch.clientY - (positions[photoId]?.offset || 0));
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!dragging) return;

    const touch = e.touches[0];
    const newOffset = touch.clientY - dragStart;
    const img = imageRefs.current[dragging];
    if (!img) return;

    const { scale } = getImageDimensions(dragging);
    const scaledHeight = img.naturalHeight * scale;
    const maxOffset = (scaledHeight - PREVIEW_SIZE) / 2;

    setPositions(prev => ({
      ...prev,
      [dragging]: {
        offset: Math.max(-maxOffset, Math.min(maxOffset, newOffset)),
        isDirty: true
      }
    }));
  };

  const handleTouchEnd = () => {
    setDragging(null);
  };

  const handleSaveCropped = async (photoId: string) => {
    const startTime = performance.now();
    console.log('[PhotoGallery] 🚀 Starting crop/save process for photo:', photoId);

    const img = imageRefs.current[photoId];
    const canvas = canvasRef.current;
    if (!img || !canvas) return;

    setSaving(photoId);
    try {
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Could not get canvas context');

      const { isPortrait, scale } = getImageDimensions(photoId);
      const offset = positions[photoId]?.offset || 0;

      const setupTime = performance.now();
      console.log(`[PhotoGallery] ⚙️ Canvas setup complete (+${(setupTime - startTime).toFixed(0)}ms)`);

      // Set canvas to output size (1000x1000)
      const outputSize = 1000;
      canvas.width = outputSize;
      canvas.height = outputSize;

      // Fill with white background (important for PNGs with transparency)
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, outputSize, outputSize);

      // Calculate source coordinates
      const scaleFactor = 1 / scale;
      const sourceSize = PREVIEW_SIZE * scaleFactor;

      let sourceX, sourceY;
      if (isPortrait) {
        sourceX = (img.naturalWidth - sourceSize) / 2;
        sourceY = (img.naturalHeight - sourceSize) / 2 - (offset * scaleFactor);
      } else {
        sourceX = (img.naturalWidth - sourceSize) / 2 - (offset * scaleFactor);
        sourceY = (img.naturalHeight - sourceSize) / 2;
      }

      // Draw cropped image on white background
      ctx.drawImage(
        img,
        sourceX,
        sourceY,
        sourceSize,
        sourceSize,
        0,
        0,
        outputSize,
        outputSize
      );

      const drawTime = performance.now();
      console.log(`[PhotoGallery] 🖼️ Image drawn to canvas (+${(drawTime - setupTime).toFixed(0)}ms)`);

      // Convert to blob
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (blob) resolve(blob);
            else reject(new Error('Failed to create blob'));
          },
          'image/jpeg',
          0.95
        );
      });

      const blobTime = performance.now();
      console.log(`[PhotoGallery] 📦 Blob created (${(blob.size / 1024).toFixed(0)}KB) (+${(blobTime - drawTime).toFixed(0)}ms)`);

      const uploadStartTime = performance.now();
      await onSaveCropped(photoId, blob);
      const uploadEndTime = performance.now();

      console.log(`[PhotoGallery] ☁️ Upload complete (+${(uploadEndTime - uploadStartTime).toFixed(0)}ms)`);
      console.log(`[PhotoGallery] ✅ Total time: ${(uploadEndTime - startTime).toFixed(0)}ms`);

      // Reset dirty flag
      setPositions(prev => ({
        ...prev,
        [photoId]: { ...prev[photoId], isDirty: false }
      }));
    } catch (error) {
      console.error('[PhotoGallery] ❌ Error saving cropped image:', error);
    } finally {
      setSaving(null);
    }
  };

  if (photos.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
        border="1px dashed"
        borderColor="grey.400"
        borderRadius={2}
      >
        <Typography variant="body2" color="text.secondary">
          No photos uploaded yet
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Grid container spacing={2}>
        {photos.map((photo) => {
          const position = positions[photo.id] || { offset: 0, isDirty: false };
          const { isPortrait } = getImageDimensions(photo.id);

          return (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={photo.id}>
              <Card>
                <Box position="relative" display="flex" justifyContent="center" pt={2} pb={1}>
                  {/* Circular preview */}
                  <Box
                    sx={{
                      width: PREVIEW_SIZE,
                      height: PREVIEW_SIZE,
                      borderRadius: '50%',
                      overflow: 'hidden',
                      border: '2px solid',
                      borderColor: photo.primary ? 'primary.main' : 'grey.300',
                      cursor: isPortrait ? (dragging === photo.id ? 'grabbing' : 'grab') : 'default',
                      userSelect: 'none',
                      position: 'relative',
                    }}
                    onMouseDown={(e) => handleMouseDown(photo.id, e)}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    onTouchStart={(e) => handleTouchStart(photo.id, e)}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                  >
                    <img
                      src={photo.thumbnailUrl || photo.web_url}
                      alt={photo.filename}
                      onLoad={(e) => handleImageLoad(photo.id, e.currentTarget)}
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: isPortrait
                          ? `translate(-50%, calc(-50% + ${position.offset}px))`
                          : `translate(calc(-50% + ${position.offset}px), -50%)`,
                        width: isPortrait ? '100%' : 'auto',
                        height: isPortrait ? 'auto' : '100%',
                        pointerEvents: 'none',
                        display: 'block',
                      }}
                      draggable={false}
                    />
                  </Box>

                  {/* Status chips */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                    }}
                  >
                    {photo.primary && (
                      <Chip
                        icon={<Star />}
                        label="Primary"
                        color="primary"
                        size="small"
                      />
                    )}

                    {uploadingJobs.has(photo.id) && (
                      <Chip
                        icon={<CloudUpload />}
                        label="Uploading..."
                        color="info"
                        size="small"
                        sx={{ animation: 'pulse 1.5s ease-in-out infinite' }}
                      />
                    )}
                  </Box>

                  <Tooltip title="Open in SharePoint">
                    <IconButton
                      size="small"
                      onClick={() => window.open(photo.web_url, '_blank')}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 1)',
                        },
                      }}
                    >
                      <Launch fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Box p={1.5}>
                  <Typography variant="body2" noWrap title={photo.filename}>
                    {photo.filename}
                  </Typography>

                  <Box display="flex" gap={1} mt={0.5}>
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(photo.file_size)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      •
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(photo.last_modified)}
                    </Typography>
                  </Box>
                </Box>

                <CardActions sx={{ flexDirection: 'column', gap: 1, px: 1.5, pb: 1.5 }}>
                  <Box display="flex" width="100%" gap={1}>
                    <Button
                      size="small"
                      startIcon={photo.primary ? <Star /> : <StarBorder />}
                      onClick={() => onSetPrimary(photo.id)}
                      disabled={disabled || photo.primary}
                      variant={photo.primary ? 'contained' : 'outlined'}
                      fullWidth
                    >
                      {photo.primary ? 'Primary' : 'Set Primary'}
                    </Button>

                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => onDelete(photo.id)}
                      disabled={disabled}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>

                  {position.isDirty && (
                    <Button
                      size="small"
                      startIcon={saving === photo.id ? <CircularProgress size={16} /> : <Save />}
                      onClick={() => handleSaveCropped(photo.id)}
                      disabled={disabled || saving === photo.id}
                      variant="contained"
                      color="success"
                      fullWidth
                    >
                      {saving === photo.id ? 'Saving...' : 'Save Cropped'}
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Hidden canvas for cropping */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </>
  );
};

export default PhotoGallery;
